import type React from "react";
import { memo, useCallback, useEffect, useMemo, type FC } from "react";
import { useEnvironmentClassEntries, type EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import { Combobox } from "@/components/flexkit/combobox";

export const EnvironmentClassSelector: FC<{
    selectedEnvironmentClassId: string;
    disabled: boolean;
    onSelectionChange: (entry: EnvironmentClassEntry) => void;
}> = memo(({ selectedEnvironmentClassId, onSelectionChange }) => {
    const { environmentClassEntries, isLoading: isLoadingEnvironmentClasses } = useEnvironmentClassEntries({
        enabled: true,
    });

    const selectedEnvironmentClass = useMemo(
        () => environmentClassEntries.find((it) => it.clazz.id === selectedEnvironmentClassId),
        [environmentClassEntries, selectedEnvironmentClassId],
    );

    const _onSelectionChangeById = useCallback(
        (environmentClassId: string) => {
            const entry = environmentClassEntries.find((it) => it.clazz.id === environmentClassId);
            if (entry) {
                onSelectionChange(entry);
            }
        },
        [environmentClassEntries, onSelectionChange],
    );

    useEffect(() => {
        if (selectedEnvironmentClassId && environmentClassEntries.length > 0) {
            _onSelectionChangeById(selectedEnvironmentClassId);
        }
    }, [selectedEnvironmentClassId, _onSelectionChangeById, environmentClassEntries]);

    let comboboxContent: React.ReactNode;
    if (isLoadingEnvironmentClasses) {
        comboboxContent = <Combobox.Loading>Loading environment classes…</Combobox.Loading>;
    } else {
        comboboxContent = (
            <Combobox.List
                items={environmentClassEntries}
                searchKeys={["runner.name", "clazz.displayName", "clazz.description"]}
                noMatchesComponent={<Combobox.Empty>No matching environment classes.</Combobox.Empty>}
            >
                {(entry) => <EnvironmentClassSelectItem key={entry.clazz.id} entry={entry} />}
            </Combobox.List>
        );
    }

    return (
        <Combobox
            value={selectedEnvironmentClass?.clazz.id ?? ""}
            onValueChange={_onSelectionChangeById}
            loading={isLoadingEnvironmentClasses}
            className="grow"
        >
            <Combobox.Value>
                {selectedEnvironmentClass ? (
                    <Combobox.ValueLabel>
                        {selectedEnvironmentClass.clazz.displayName}&nbsp;
                        <span className="text-content-muted">{selectedEnvironmentClass.clazz.description}</span>
                    </Combobox.ValueLabel>
                ) : (
                    <Combobox.ValueLabel className="text-content-muted">
                        Select an environment class
                    </Combobox.ValueLabel>
                )}
            </Combobox.Value>
            <Combobox.Popover className="w-128 max-w-[calc(100vw-1rem)]">{comboboxContent}</Combobox.Popover>
        </Combobox>
    );
});
EnvironmentClassSelector.displayName = "EnvironmentClassSelector";

const EnvironmentClassSelectItem: FC<{
    entry: EnvironmentClassEntry;
}> = memo(({ entry }) => {
    return (
        <Combobox.ListItem value={entry.clazz.id}>
            <Combobox.ListItemTitle>{entry.runner.name}</Combobox.ListItemTitle>
            <Combobox.ListItemDescription>
                {entry.clazz.displayName} • {entry.clazz.description}
            </Combobox.ListItemDescription>
        </Combobox.ListItem>
    );
});
EnvironmentClassSelectItem.displayName = "EnvironmentClassSelectItem";
